import {
  Badge,
  Button,
  Card,
  Group,
  Text,
  Title,
} from '@mantine/core';
import { notify } from '@/lib/notify';
import { IconPlus } from '@tabler/icons-react';
import { useCallback, useMemo, useState } from 'react';
import { UnifyTable, type UnifyTableColumn } from '@/components/UnifyTable';
import { currentInsuranceCards, type InsuranceCardDocument } from '@/lib/billing/insuranceCards';
import type { InsuranceEntry, InsurancePriority } from './types';
import { maskId, priorityLabel, prioritySortKey, verificationBadge } from './helpers';
import { emptyInsuranceDraft, InsurancePlanModal } from './InsurancePlanModal';

export interface InsuranceSectionProps {
  insurances: InsuranceEntry[];
  onInsurancesChange: (insurances: InsuranceEntry[]) => void;
  insuranceTypeOptions?: { value: string; label: string }[];
  /** When set, enables per-plan card photos in the modal and async save. */
  insuranceCards?: InsuranceCardDocument[];
  onSaveInsurance?: (
    draft: InsuranceEntry,
    context: {
      mode: 'add' | 'edit';
      editingId: string | null;
      cardFiles: { front?: File; back?: File };
    },
  ) => Promise<void>;
  /** Live FHIR remove — cancels Coverage and refreshes parent data. */
  onRemoveInsurance?: (insuranceId: string) => Promise<void>;
}

export function InsuranceSection({
  insurances,
  onInsurancesChange,
  insuranceTypeOptions,
  insuranceCards,
  onSaveInsurance,
  onRemoveInsurance,
}: InsuranceSectionProps): JSX.Element {
  const [saving, setSaving] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [insuranceEditOpen, setInsuranceEditOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('edit');
  const [insuranceEditingId, setInsuranceEditingId] = useState<string | null>(null);
  const [draftInsurance, setDraftInsurance] = useState<InsuranceEntry | null>(null);

  const sortedInsurances = useMemo(
    () => [...insurances].sort((a, b) => prioritySortKey(a.priority) - prioritySortKey(b.priority)),
    [insurances],
  );
  const usedPriorities = useMemo(() => new Set(insurances.map((i) => i.priority)), [insurances]);
  const canAddInsurance = insurances.length < 3;

  const openAddInsurance = () => {
    const nextPriority: InsurancePriority | null = !usedPriorities.has('primary')
      ? 'primary'
      : !usedPriorities.has('secondary')
        ? 'secondary'
        : !usedPriorities.has('tertiary')
          ? 'tertiary'
          : null;
    if (!nextPriority) {
      notify({ type: 'warning', title: 'Limit reached', message: 'You can have up to three insurance plans.' });
      return;
    }
    setModalMode('add');
    setInsuranceEditingId(null);
    setDraftInsurance(emptyInsuranceDraft(nextPriority));
    setInsuranceEditOpen(true);
  };

  const openEditInsurance = (ins: InsuranceEntry) => {
    setModalMode('edit');
    setInsuranceEditingId(ins.id);
    setDraftInsurance({ ...ins });
    setInsuranceEditOpen(true);
  };

  const closeModal = () => {
    setInsuranceEditOpen(false);
    setDraftInsurance(null);
    setInsuranceEditingId(null);
  };

  const saveInsurance = async (
    draft: InsuranceEntry,
    cardFiles: { front?: File; back?: File },
  ) => {
    if (!draft.provider) {
      notify({ type: 'error', title: 'Missing provider', message: 'Please select an insurance provider.' });
      return;
    }
    if (!draft.memberId.trim()) {
      notify({ type: 'error', title: 'Missing member ID', message: 'Please enter a member ID.' });
      return;
    }
    if (!draft.subscriberIsClient) {
      if (!draft.subscriberFirstName.trim() || !draft.subscriberLastName.trim() || !draft.subscriberDob) {
        notify({ type: 'error', title: 'Missing subscriber details', message: 'Enter subscriber first name, last name, and date of birth.' });
        return;
      }
      if (!draft.subscriberRelationship) {
        notify({ type: 'error', title: 'Missing relationship', message: 'Select your relationship to the subscriber.' });
        return;
      }
    }
    const priorityTakenByOther = insurances.some(
      (i) => i.priority === draft.priority && i.id !== (insuranceEditingId ?? ''),
    );
    if (priorityTakenByOther) {
      notify({
        type: 'error',
        title: 'Priority already used',
        message: `A ${priorityLabel(draft.priority)} plan already exists.`,
      });
      return;
    }

    if (onSaveInsurance) {
      setSaving(true);
      try {
        await onSaveInsurance(draft, {
          mode: modalMode,
          editingId: insuranceEditingId,
          cardFiles,
        });
        closeModal();
        notify({ type: 'success', title: 'Saved', message: 'Insurance details saved.' });
      } catch {
        notify({
          type: 'error',
          title: 'Could not save insurance',
          message: 'Check your permissions and try again.',
        });
      } finally {
        setSaving(false);
      }
      return;
    }

    const updated = (() => {
      const idx = insurances.findIndex((p) => p.id === (insuranceEditingId ?? ''));
      if (idx >= 0) {
        const next = [...insurances];
        next[idx] = { ...draft };
        return next;
      }
      return [...insurances, { ...draft }];
    })();
    onInsurancesChange(updated);
    closeModal();
    notify({ type: 'success', title: 'Saved', message: 'Insurance details saved.' });
  };

  const showCardPhotos = Boolean(onSaveInsurance);

  const editingCards =
    showCardPhotos && insuranceEditingId && insuranceCards
      ? currentInsuranceCards(
          insuranceCards.filter((c) => c.coverageRef === `Coverage/${insuranceEditingId}`),
        )
      : undefined;

  const removeInsurance = useCallback(async (ins: InsuranceEntry) => {
    if (onRemoveInsurance) {
      setRemovingId(ins.id);
      try {
        await onRemoveInsurance(ins.id);
        notify({
          type: 'success',
          title: 'Removed',
          message: `${priorityLabel(ins.priority)} insurance removed.`,
        });
      } catch {
        notify({
          type: 'error',
          title: 'Could not remove insurance',
          message: 'Check your permissions and try again.',
        });
      } finally {
        setRemovingId(null);
      }
      return;
    }
    onInsurancesChange(insurances.filter((p) => p.id !== ins.id));
    notify({
      type: 'success',
      title: 'Removed',
      message: `${priorityLabel(ins.priority)} insurance removed.`,
    });
  }, [insurances, onInsurancesChange, onRemoveInsurance]);

  const insuranceColumns = useMemo((): UnifyTableColumn<InsuranceEntry>[] => {
    return [
      {
        key: 'priority',
        label: 'Priority',
        render: (ins) => (
          <Badge variant="light" color="blue" size="sm">
            {priorityLabel(ins.priority)}
          </Badge>
        ),
        getValue: (ins) => priorityLabel(ins.priority),
      },
      {
        key: 'provider',
        label: 'Provider',
        render: (ins) => (
          <>
            <Text size="sm" fw={600}>
              {ins.provider ?? '—'}
            </Text>
            {ins.groupId ? (
              <Text size="xs" c="gray.6">
                Group {maskId(ins.groupId)}
              </Text>
            ) : null}
          </>
        ),
        getValue: (ins) => ins.provider ?? '',
      },
      {
        key: 'memberId',
        label: 'Member ID',
        render: (ins) => <Text size="sm">{maskId(ins.memberId)}</Text>,
        getValue: (ins) => ins.memberId,
      },
      {
        key: 'subscriber',
        label: 'Subscriber',
        render: (ins) => {
          const subscriberLabel = ins.subscriberIsClient
            ? 'Self'
            : `${ins.subscriberFirstName || '—'} ${ins.subscriberLastName || ''}`.trim();
          return (
            <>
              <Text size="sm">{subscriberLabel || '—'}</Text>
              {!ins.subscriberIsClient && ins.subscriberRelationship && (
                <Text size="xs" c="gray.6">
                  Relationship:{' '}
                  {ins.subscriberRelationship === 'spouse'
                    ? 'Spouse'
                    : ins.subscriberRelationship === 'child'
                      ? 'Parent / guardian'
                      : 'Other'}
                </Text>
              )}
            </>
          );
        },
        getValue: (ins) =>
          ins.subscriberIsClient
            ? 'Self'
            : `${ins.subscriberFirstName || ''} ${ins.subscriberLastName || ''}`.trim(),
      },
      {
        key: 'status',
        label: 'Status',
        render: (ins) => {
          const v = verificationBadge(ins.verificationStatus);
          return (
            <Badge variant="light" color={v.color} size="sm">
              {v.label}
            </Badge>
          );
        },
        getValue: (ins) => verificationBadge(ins.verificationStatus).label,
      },
      {
        key: 'actions',
        label: '',
        sortable: false,
        render: (ins) => (
          <Group justify="flex-end" wrap="nowrap">
            <Button size="xs" variant="light" onClick={() => openEditInsurance(ins)}>
              Edit
            </Button>
            <Button
              size="xs"
              variant="outline"
              color="red"
              loading={removingId === ins.id}
              disabled={removingId != null && removingId !== ins.id}
              onClick={() => void removeInsurance(ins)}
            >
              Remove
            </Button>
          </Group>
        ),
        getValue: () => '',
      },
    ];
  }, [removingId, removeInsurance]);

  return (
    <>
      <Card withBorder>
        <Group justify="space-between" mb="md">
          <div>
            <Title order={4}>Insurance</Title>
            <Text size="sm" c="gray.6" mt="xs">
              Up to three plans (primary, secondary, tertiary)
            </Text>
          </div>
          <Group gap="sm">
            <Button leftSection={<IconPlus size={16} />} disabled={!canAddInsurance} onClick={openAddInsurance}>
              Add plan
            </Button>
          </Group>
        </Group>
        {sortedInsurances.length === 0 ? (
          <Text size="sm" c="gray.6">No insurance on file.</Text>
        ) : (
          <UnifyTable
            withPaper={false}
            columns={insuranceColumns}
            data={sortedInsurances}
            getRowKey={(ins) => ins.id}
          />
        )}
      </Card>

      <InsurancePlanModal
        opened={insuranceEditOpen}
        mode={modalMode}
        draft={draftInsurance}
        insuranceTypeOptions={insuranceTypeOptions}
        showCardPhotos={showCardPhotos}
        memberIdHint={
          modalMode === 'edit' && draftInsurance?.memberId
            ? maskId(draftInsurance.memberId)
            : undefined
        }
        cardPhotos={showCardPhotos ? (editingCards ?? {}) : undefined}
        saving={saving}
        onClose={closeModal}
        onDraftChange={setDraftInsurance}
        onSubmit={saveInsurance}
      />
    </>
  );
}
